import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Group,
  Stack,
  Text,
  Badge,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  Slider,
  Button,
  NumberInput,
  Menu,
  Portal,
} from '@mantine/core';
import {
  IconAppWindow,
  IconCircleDot,
  IconRefresh,
  IconZoomIn,
  IconZoomOut,
  IconMaximize,
  IconMinimize,
  IconSparkles,
} from '@tabler/icons-react';
import type { WebBotProviderData } from '@/API';
import { calculateOptimalPreviewSize } from './webBotPreviewUtils';

export interface WebBotLivePreviewProps {
  data: Partial<WebBotProviderData>;
  botProviderId?: string;
  workspaceId?: string;
}

type ResizeDirection =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export function WebBotLivePreview({
  data,
  botProviderId,
}: WebBotLivePreviewProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [zoomPercent, setZoomPercent] = useState<number | null>(null);
  const [customSize, setCustomSize] = useState<{ width: number; height: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const isCustomUiConfigured = !!data.customUi?.enabled;
  const [previewTarget, setPreviewTarget] = useState<'custom' | 'standard'>('custom');

  useEffect(() => {
    if (isCustomUiConfigured) {
      setPreviewTarget('custom');
    }
  }, [isCustomUiConfigured]);

  const effectiveData = useMemo(() => {
    if (!isCustomUiConfigured || previewTarget === 'custom') {
      return data;
    }
    return {
      ...data,
      customUi: {
        ...data.customUi,
        enabled: false,
      },
    };
  }, [data, isCustomUiConfigured, previewTarget]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 600, height: 700 });

  // Escape key to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Calculate recommended default preview size based on window & extension dimensions
  const optimalSize = useMemo(() => calculateOptimalPreviewSize(data), [data]);

  const canvasWidth = customSize?.width ?? optimalSize.width;
  const canvasHeight = customSize?.height ?? optimalSize.height;

  // Monitor available space in the preview column container
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;

    const updateSize = (rect: DOMRectReadOnly | DOMRect) => {
      if (rect.width > 0 && rect.height > 0) {
        setContainerSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateSize(el.getBoundingClientRect());

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateSize(entry.contentRect);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [isFullscreen]);

  // Reset zoom to auto-fit whenever fullscreen state toggles
  useEffect(() => {
    setZoomPercent(null);
  }, [isFullscreen]);

  // Ensure cakechat-widget custom element script is loaded
  useEffect(() => {
    const scriptId = 'web-bot-script';
    if (!document.getElementById(scriptId) && !customElements.get('cakechat-widget')) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `${window.origin}/api/WebBot/script`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  // Calculate auto-fit scale
  const autoFitScale = useMemo(() => {
    const availW = Math.max(containerSize.width - 32, 100);
    const availH = Math.max(containerSize.height - 32, 100);
    const scaleX = availW / canvasWidth;
    const scaleY = availH / canvasHeight;
    return Math.min(scaleX, scaleY, 1);
  }, [containerSize.width, containerSize.height, canvasWidth, canvasHeight]);

  const autoFitPercent = useMemo(() => {
    return Math.max(25, Math.min(150, Math.round(autoFitScale * 100)));
  }, [autoFitScale]);

  const currentZoom = zoomPercent ?? autoFitPercent;
  const scale = currentZoom / 100;

  // Interactive drag resize handler (Chrome DevTools style 4 corners & 4 edges)
  const handleResizeStart = useCallback(
    (direction: ResizeDirection) => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const startW = canvasWidth;
      const startH = canvasHeight;
      const currentScale = scale;

      setIsResizing(true);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const deltaX = (moveEvent.clientX - startX) / currentScale;
        const deltaY = (moveEvent.clientY - startY) / currentScale;

        let nextW = startW;
        let nextH = startH;

        if (direction === 'right' || direction === 'top-right' || direction === 'bottom-right') {
          nextW = startW + deltaX;
        } else if (direction === 'left' || direction === 'top-left' || direction === 'bottom-left') {
          nextW = startW - deltaX;
        }

        if (direction === 'bottom' || direction === 'bottom-left' || direction === 'bottom-right') {
          nextH = startH + deltaY;
        } else if (direction === 'top' || direction === 'top-left' || direction === 'top-right') {
          nextH = startH - deltaY;
        }

        setCustomSize({
          width: Math.max(320, Math.min(2400, Math.round(nextW))),
          height: Math.max(400, Math.min(2400, Math.round(nextH))),
        });
      };

      const handlePointerUp = () => {
        setIsResizing(false);
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [canvasWidth, canvasHeight, scale]
  );

  const previewContent = (
    <Paper
      withBorder={!isFullscreen}
      p={isFullscreen ? 'md' : 'xs'}
      radius={isFullscreen ? 0 : 'md'}
      style={
        isFullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
              width: '100vw',
              backgroundColor: 'var(--mantine-color-body)',
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              backgroundColor: 'var(--mantine-color-body)',
            }
      }
    >
      {/* Top Controls Toolbar */}
      <Stack gap="xs" mb="xs" style={{ flexShrink: 0 }}>
        <Group justify="space-between" align="center" wrap="wrap" gap="xs">
          <Group gap="xs">
            <Text size="xs" fw={700} c="dimmed">
              正式元件即時預覽 (Live Preview)
            </Text>
            <Badge size="xs" variant="light" color={isOpen ? 'blue' : 'gray'}>
              {isOpen ? '對話視窗展開' : '按鈕收合狀態'}
            </Badge>
            <Badge size="xs" variant="outline" color="gray">
              {canvasWidth} × {canvasHeight} px
            </Badge>
            {isFullscreen && (
              <Badge size="xs" variant="filled" color="blue">
                全螢幕模式
              </Badge>
            )}
          </Group>

          <Group gap={6}>
            {isCustomUiConfigured && (
              <SegmentedControl
                size="xs"
                value={previewTarget}
                onChange={(val) => setPreviewTarget(val as 'custom' | 'standard')}
                data={[
                  {
                    label: (
                      <Group gap={4} wrap='nowrap'>
                        <IconSparkles size={13} />
                        <Text size="xs">自訂 UI 預覽</Text>
                      </Group>
                    ),
                    value: 'custom',
                  },
                  {
                    label: (
                      <Group gap={4} wrap='nowrap'>
                        <IconAppWindow size={13} />
                        <Text size="xs">標準元件</Text>
                      </Group>
                    ),
                    value: 'standard',
                  },
                ]}
              />
            )}

            <SegmentedControl
              size="xs"
              value={isOpen ? 'open' : 'closed'}
              onChange={(val) => setIsOpen(val === 'open')}
              data={[
                {
                  label: (
                    <Group gap={4}>
                      <IconAppWindow size={13} />
                      <Text size="xs">展開視窗</Text>
                    </Group>
                  ),
                  value: 'open',
                },
                {
                  label: (
                    <Group gap={4}>
                      <IconCircleDot size={13} />
                      <Text size="xs">收合按鈕</Text>
                    </Group>
                  ),
                  value: 'closed',
                },
              ]}
            />
            <Tooltip label="重新載入預覽">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setRefreshKey((prev) => prev + 1)}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={isFullscreen ? '結束全螢幕 (Esc)' : '全螢幕預覽'}>
              <ActionIcon
                variant={isFullscreen ? 'filled' : 'subtle'}
                color="blue"
                size="sm"
                onClick={() => setIsFullscreen((prev) => !prev)}
              >
                {isFullscreen ? <IconMinimize size={14} /> : <IconMaximize size={14} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Dimension & Zoom Controls Toolbar */}
        <Group justify="space-between" align="center" px={4} wrap="wrap" gap="xs">
          {/* Custom Dimension Inputs & Presets */}
          <Group gap={6} align="center">
            <Text size="xs" c="dimmed" fw={500}>
              尺寸:
            </Text>
            <NumberInput
              size="xs"
              w={75}
              min={320}
              max={2200}
              step={20}
              value={canvasWidth}
              onChange={(val) =>
                setCustomSize({
                  width: Number(val) || optimalSize.width,
                  height: canvasHeight,
                })
              }
              aria-label="寬度"
            />
            <Text size="xs" c="dimmed">
              ×
            </Text>
            <NumberInput
              size="xs"
              w={75}
              min={400}
              max={2200}
              step={20}
              value={canvasHeight}
              onChange={(val) =>
                setCustomSize({
                  width: canvasWidth,
                  height: Number(val) || optimalSize.height,
                })
              }
              aria-label="高度"
            />
            <Tooltip label="重設為符合目前聊天室與擴充套件的最佳預設尺寸">
              <Button
                variant={customSize === null ? 'light' : 'subtle'}
                color="blue"
                size="compact-xs"
                onClick={() => setCustomSize(null)}
              >
                🎯 最適尺寸
              </Button>
            </Tooltip>
            <Menu shadow="md" width={160} position="bottom-start">
              <Menu.Target>
                <Button variant="subtle" color="gray" size="compact-xs">
                  常用尺寸 ▾
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setCustomSize({ width: 420, height: 700 })}>
                  精簡 420 × 700
                </Menu.Item>
                <Menu.Item onClick={() => setCustomSize({ width: 600, height: 750 })}>
                  中型 600 × 750
                </Menu.Item>
                <Menu.Item onClick={() => setCustomSize({ width: 800, height: 800 })}>
                  寬版 800 × 800
                </Menu.Item>
                <Menu.Item onClick={() => setCustomSize({ width: 1024, height: 850 })}>
                  桌面 1024 × 850
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Zoom Scaling Controls */}
          <Group gap={6} align="center">
            <Text size="xs" c="dimmed" fw={500}>
              縮放:
            </Text>
            <Tooltip label="縮小 10%">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => setZoomPercent(Math.max(25, currentZoom - 10))}
                disabled={currentZoom <= 25}
              >
                <IconZoomOut size={14} />
              </ActionIcon>
            </Tooltip>
            <Slider
              size="xs"
              w={85}
              min={25}
              max={150}
              step={5}
              value={currentZoom}
              onChange={(val) => setZoomPercent(val)}
              label={(val) => `${val}%`}
            />
            <Tooltip label="放大 10%">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => setZoomPercent(Math.min(150, currentZoom + 10))}
                disabled={currentZoom >= 150}
              >
                <IconZoomIn size={14} />
              </ActionIcon>
            </Tooltip>
            <Badge size="xs" variant="light" color="blue" w={46} style={{ textAlign: 'center' }}>
              {currentZoom}%
            </Badge>

            <Button
              variant={zoomPercent === null || currentZoom === autoFitPercent ? 'light' : 'subtle'}
              color="blue"
              size="compact-xs"
              onClick={() => setZoomPercent(null)}
            >
              🎯 最佳適配 ({autoFitPercent}%)
            </Button>
            <Button
              variant={currentZoom === 100 ? 'light' : 'subtle'}
              color="gray"
              size="compact-xs"
              onClick={() => setZoomPercent(100)}
            >
              100% 原尺寸
            </Button>
          </Group>
        </Group>
      </Stack>

      {/* Canvas Viewport */}
      <Box
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: 0,
          maxHeight: 'none',
          height: isFullscreen ? 'calc(100vh - 120px)' : '100%',
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 8,
          position: 'relative',
          overflow: 'auto',
          backgroundColor: '#f1f3f5',
          backgroundImage:
            'radial-gradient(#dee2e6 1px, transparent 1px), radial-gradient(#dee2e6 1px, #f1f3f5 1px)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 10px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          userSelect: isResizing ? 'none' : 'auto',
        }}
      >
        {/* Scaled Frame Box */}
        <Box
          style={{
            width: `${canvasWidth * scale}px`,
            height: `${canvasHeight * scale}px`,
            position: 'relative',
            flexShrink: 0,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            borderRadius: 8,
            border: '1px solid #ced4da',
            backgroundColor: '#ffffff',
          }}
        >
          {/* Inner Virtual Viewport with scale transform */}
          <Box
            style={{
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
              overflow: 'hidden',
              backgroundColor: '#ffffff',
              borderRadius: 7,
            }}
          >
            {React.createElement('cakechat-widget', {
              key: `${botProviderId || 'draft'}_${refreshKey}_${isOpen ? 'open' : 'closed'}_${previewTarget}`,
              'bot-provider-id': botProviderId || '',
              'host': window.origin,
              'preview-config': JSON.stringify(effectiveData),
              'preview-mode': 'true',
              'contained': 'true',
              'default-open': isOpen ? 'true' : 'false',
              'is-mobile':
                canvasWidth <=
                ((data.responsive?.breakpoint ?? (data as any).responsiveBreakpoint) != null &&
                String(data.responsive?.breakpoint ?? (data as any).responsiveBreakpoint).trim() !== '' &&
                !isNaN(Number(data.responsive?.breakpoint ?? (data as any).responsiveBreakpoint))
                  ? Number(data.responsive?.breakpoint ?? (data as any).responsiveBreakpoint)
                  : 768)
                  ? 'true'
                  : 'false',
              style: {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
              },
            })}
          </Box>

          {/* Chrome DevTools style 4 Edge Grab Bars & Drag Zones */}
          {/* Top Edge */}
          <Box
            onPointerDown={handleResizeStart('top')}
            style={{
              position: 'absolute',
              top: -6,
              left: 12,
              right: 12,
              height: 12,
              cursor: 'ns-resize',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box style={{ width: 28, height: 4, borderRadius: 2, backgroundColor: '#adb5bd' }} />
          </Box>

          {/* Bottom Edge */}
          <Box
            onPointerDown={handleResizeStart('bottom')}
            style={{
              position: 'absolute',
              bottom: -6,
              left: 12,
              right: 12,
              height: 12,
              cursor: 'ns-resize',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box style={{ width: 28, height: 4, borderRadius: 2, backgroundColor: '#adb5bd' }} />
          </Box>

          {/* Left Edge */}
          <Box
            onPointerDown={handleResizeStart('left')}
            style={{
              position: 'absolute',
              top: 12,
              bottom: 12,
              left: -6,
              width: 12,
              cursor: 'ew-resize',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: '#adb5bd' }} />
          </Box>

          {/* Right Edge */}
          <Box
            onPointerDown={handleResizeStart('right')}
            style={{
              position: 'absolute',
              top: 12,
              bottom: 12,
              right: -6,
              width: 12,
              cursor: 'ew-resize',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box style={{ width: 4, height: 28, borderRadius: 2, backgroundColor: '#adb5bd' }} />
          </Box>

          {/* Chrome DevTools style 4 Corner Handles */}
          {/* Top-Left Corner */}
          <Box
            onPointerDown={handleResizeStart('top-left')}
            style={{
              position: 'absolute',
              top: -6,
              left: -6,
              width: 14,
              height: 14,
              backgroundColor: '#ffffff',
              border: '2px solid var(--mantine-color-blue-6)',
              borderRadius: 3,
              cursor: 'nwse-resize',
              zIndex: 12,
              boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
            }}
          />

          {/* Top-Right Corner */}
          <Box
            onPointerDown={handleResizeStart('top-right')}
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 14,
              height: 14,
              backgroundColor: '#ffffff',
              border: '2px solid var(--mantine-color-blue-6)',
              borderRadius: 3,
              cursor: 'nesw-resize',
              zIndex: 12,
              boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
            }}
          />

          {/* Bottom-Left Corner */}
          <Box
            onPointerDown={handleResizeStart('bottom-left')}
            style={{
              position: 'absolute',
              bottom: -6,
              left: -6,
              width: 14,
              height: 14,
              backgroundColor: '#ffffff',
              border: '2px solid var(--mantine-color-blue-6)',
              borderRadius: 3,
              cursor: 'nesw-resize',
              zIndex: 12,
              boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
            }}
          />

          {/* Bottom-Right Corner */}
          <Box
            onPointerDown={handleResizeStart('bottom-right')}
            style={{
              position: 'absolute',
              bottom: -6,
              right: -6,
              width: 14,
              height: 14,
              backgroundColor: '#ffffff',
              border: '2px solid var(--mantine-color-blue-6)',
              borderRadius: 3,
              cursor: 'nwse-resize',
              zIndex: 12,
              boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
            }}
          />
        </Box>
      </Box>
    </Paper>
  );

  return isFullscreen ? <Portal>{previewContent}</Portal> : previewContent;
}
